import { Reveal, Eyebrow, SectionTitle } from "@/components/ui-bits";
import Testimonios from "@/components/sections/Testimonios";

const fichaTecnica = [
  ["Categoría", "Largometraje de ficción"],
  ["Duración", "95 minutos"],
  ["Empresa productora", "NG Films SAC — Lima, Perú"],
  ["Rodaje", "Noviembre 2026 — Lima, Perú"],
  ["Estreno estimado", "Junio – agosto 2027 — Perú"],
  ["Directora y coguionista", "Camila Vidal"],
  ["Autora y coguionista", "Belinda Palacios"],
  ["Protagonista", "Silvana Cañote"],
  ["Producción ejecutiva", "Silvana Cañote, Belinda Palacios"],
  ["Dirección de fotografía", "Isabel Mazzolini"],
  ["Dirección de arte", "María Cristina Martínez"],
];

export default function Pelicula() {
  return (
    <section id="la-pelicula" data-testid="section-pelicula" className="bg-[#F2EDE3]">
      {/* 5. El problema — ahora abre el bloque de La Película (ancla del menú) */}
      <div className="pt-24 sm:pt-32 pb-12 sm:pb-16 border-t border-[#e2dccd] bg-[#F2EDE3]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          {/* PARTE 1 — la voz intrusiva */}
          <Reveal>
            <div className="relative overflow-hidden">
              {/* ecos difuminados de fondo */}
              <span aria-hidden className="pointer-events-none select-none absolute -top-4 -left-2 font-display uppercase leading-none text-[22vw] sm:text-[13rem] text-[#6E6A66]/[0.06]">gorda</span>
              <span aria-hidden className="pointer-events-none select-none absolute top-1/2 left-1/3 -translate-y-1/2 font-display uppercase leading-none text-[18vw] sm:text-[9rem] text-[#6E6A66]/[0.05]">odio</span>
              <span aria-hidden className="pointer-events-none select-none absolute bottom-0 right-0 font-display uppercase leading-none text-[20vw] sm:text-[11rem] text-[#C20A78]/[0.12]">horrible</span>

              {/* pensamientos superpuestos y compactos */}
              <div className="relative z-10">
                <p className="font-bold tracking-tight text-[#C20A78] text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">
                  Estoy gorda.
                </p>
                <p className="font-bold tracking-tight text-[#141414] text-3xl sm:text-5xl lg:text-6xl leading-[0.95] sm:ml-[20%] -mt-1 sm:-mt-2">
                  Odio mis brazos.
                </p>
                <p className="font-bold tracking-tight text-[#6E6A66] text-2xl sm:text-4xl lg:text-5xl leading-[0.95] sm:ml-[6%] -mt-0.5">
                  Odio mi barriga, mi cara, mi cuerpo.
                </p>
                <p className="font-bold italic tracking-tight text-[#141414] text-4xl sm:text-6xl lg:text-7xl leading-[0.95] sm:ml-[32%] -mt-1">
                  Me veo horrible.
                </p>
              </div>
            </div>
          </Reveal>

          {/* PARTE 2 — separador */}
          <div className="my-12 sm:my-16 h-px w-full bg-[#141414]/15" />

          {/* PARTE 3 — texto explicativo, un solo texto centrado */}
          <Reveal>
            <div className="max-w-3xl mx-auto text-center space-y-5 text-base sm:text-lg leading-relaxed text-[#141414]/90">
              <p>
                Más allá de la ficción, la repetición de ideas destructivas se
                convierte en una pesadilla real que nos persigue a todas las mujeres.
                En el Perú, miles de niñas, adolescentes y jóvenes adultas pierden su
                potencial académico, emocional y profesional por inseguridades
                corporales que las hacen vulnerables a caer en relaciones tóxicas, de
                dependencia y abuso emocional.
              </p>
              <p>
                Esto no es solo un problema individual: es un costo altísimo para el
                país en salud mental, productividad y desarrollo.
              </p>
            </div>
          </Reveal>

          {/* PARTE 4 — frase de cierre */}
          <Reveal delay={0.1}>
            <p className="mt-12 sm:mt-16 max-w-3xl mx-auto text-center font-display uppercase text-[#141414] text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Necesitamos narrativas peruanas que les den a las mujeres jóvenes
              herramientas para quererse y respetarse.
            </p>
          </Reveal>
        </div>
      </div>

      {/* 3. Apertura */}
      <div className="pt-8 sm:pt-10 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center">
              <Eyebrow>La Película</Eyebrow>
              <h2 className="font-display uppercase text-[#C20A78] text-5xl sm:text-7xl lg:text-8xl mt-4 mx-auto max-w-5xl">
                El siguiente paso: la película
              </h2>
            </div>
          </Reveal>
          <Testimonios />
        </div>
      </div>

      {/* 6. Sinopsis */}
      <div className="pt-12 sm:pt-16 pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <SectionTitle tone="gray">Sinopsis</SectionTitle>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-[#141414]/85">
              <p>
                Tali es una joven adulta que creció sintiéndose «la niña gordita»
                entre sus amigas, convencida de que su cuerpo era algo que debía
                corregir. Esa inseguridad la volvió vulnerable: primero ante Felipe,
                un catequista mayor que la presiona y la manipula, y años más tarde
                ante Tomás, un enamorado que confunde el control con el amor.
              </p>
              <p>
                Para encajar en los mundos de otros, Tali va cediendo terreno hasta
                perder de vista su verdadero deseo –estudiar diseño de modas– y
                olvida su propia voz.
              </p>
              <p>
                Por el camino, su relación con la comida y con su cuerpo se vuelve
                dañina, hasta que una crisis de salud mental, frenada a tiempo, la
                obliga a detenerse y a emprender el camino más difícil: el de
                reconstruirse.
              </p>
              <p>
                Con terapia y el apoyo de su familia y sus amigas, Tali recupera la
                confianza, retoma el diseño y presenta su primer desfile: esta vez, al
                fin, ha aprendido a quererse.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src="/still-tali.png"
              alt="Tali, protagonista de la película Niñagordita"
              className="w-full max-w-md mx-auto lg:ml-auto rounded-2xl shadow-xl shadow-[#c20a78]/10 object-cover"
              draggable={false}
            />
          </Reveal>
        </div>
      </div>

      {/* 7. Temas */}
      <div className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionTitle tone="gray" className="text-center">Temas de la película</SectionTitle>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 grid sm:grid-cols-3 gap-6">
              {[
                { src: "/tema-1-espejo.png", alt: "Tali mirándose sola en el espejo de cuerpo entero" },
                { src: "/tema-2-piscina.png", alt: "Dos jóvenes junto a la piscina" },
                { src: "/tema-3-espejito.png", alt: "Tali mirándose en un espejo de mano" },
              ].map((img) => (
                <img
                  key={img.src}
                  src={img.src}
                  alt={img.alt}
                  className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg shadow-[#c20a78]/10"
                  draggable={false}
                />
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 max-w-4xl mx-auto space-y-5 text-base sm:text-lg leading-relaxed text-[#141414]/85">
              <p>
                Niñagordita aborda temas como las inseguridades, la presión estética y
                la búsqueda de aceptación que experimentan adolescentes y jóvenes
                adultas; problemáticas que hoy cobran aún más fuerza en un mundo
                dominado por las redes sociales y referentes inalcanzables. Es una
                película que interpela directamente nuestra
                época al conectar cuerpo, salud mental y vínculos afectivos con las
                tensiones culturales contemporáneas, mostrando cómo estas presiones
                pueden derivar en relaciones tóxicas, dependencia y abuso emocional.
              </p>
              <p>
                Como película, parte de una mirada femenina y evoluciona hacia un
                thriller íntimo, que desemboca finalmente en un mensaje central: la
                importancia del amor propio y la confianza en una misma para
                desarrollarnos plenamente y cumplir nuestros sueños.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* 8. Ficha técnica */}
      <div className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionTitle tone="gray">Ficha técnica</SectionTitle>
          </Reveal>
          <Reveal delay={0.1}>
            <dl className="mt-8 divide-y divide-[#e2dccd] border-t border-b border-[#e2dccd]">
              {fichaTecnica.map(([k, v]) => (
                <div key={k} className="grid sm:grid-cols-3 gap-x-2 gap-y-0.5 py-2">
                  <dt className="text-sm font-semibold uppercase tracking-wide text-[#6E6A66] leading-snug">{k}</dt>
                  <dd className="sm:col-span-2 text-base text-[#141414]/85 leading-snug">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
